import React, { useState, useEffect, useCallback } from 'react';
import {
  verifyLicense,
  verifyInstitutionCode,
  joinClassByCode,
  takeJoinedSeat,
  revalidateInstitutionSession,
  isLicenseValid,
  getLicenseInfo,
  clearLicense,
  activateFreeTrial,
  watchSeatHolder,
  validatePersonalSession,
} from '../utils/licenseService';
import { TAKEN_NOTICE, PERSONAL_RECHECK_MS } from '../lib/deviceSession';

/* ── 온보딩 오버레이 (무료체험 진입 시 3단계 안내) ── */
const OnboardingOverlay = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [fadeClass, setFadeClass] = useState('opacity-0');

  useEffect(() => {
    const t = setTimeout(() => setFadeClass('opacity-100'), 50);
    return () => clearTimeout(t);
  }, []);

  const steps = [
    {
      title: '시설을 선택합니다',
      description: '반도체 교육센터, 트레이닝센터, 분석센터, 플라즈마 심화교육 중 원하는 시설을 선택할 수 있습니다.',
      highlight: 'Education Center',
      image: '/onboarding-center.webp',
      badge: '1 / 2',
      accent: 'cyan',
    },
    {
      title: '과정을 선택합니다',
      description: '교육센터에서 3일 속성, 7일 기본, 14일 심화 과정을 선택합니다. 무료 체험에서는 3일 속성 과정의 Day 1을 체험할 수 있습니다.',
      highlight: '3日 속성 과정 · Day 1',
      image: '/onboarding-course.webp',
      badge: '2 / 2',
      accent: 'blue',
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  const accentColors = {
    cyan: { bg: 'from-cyan-600 to-cyan-800', border: 'border-cyan-500', text: 'text-cyan-400', btn: 'bg-cyan-600 hover:bg-cyan-700', glow: 'bg-cyan-500' },
    blue: { bg: 'from-blue-600 to-blue-800', border: 'border-blue-500', text: 'text-blue-400', btn: 'bg-blue-600 hover:bg-blue-700', glow: 'bg-blue-500' },
    green: { bg: 'from-green-600 to-green-800', border: 'border-green-500', text: 'text-green-400', btn: 'bg-green-600 hover:bg-green-700', glow: 'bg-green-500' },
  };
  const colors = accentColors[current.accent];

  const handleNext = () => {
    if (isLast) {
      setFadeClass('opacity-0');
      setTimeout(() => onComplete(), 400);
    } else {
      setStep(s => s + 1);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-400 ${fadeClass}`}>
      <div className="w-full max-w-xl mx-4">
        <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
          {/* 상단 배지 */}
          <div className={`bg-gradient-to-r ${colors.bg} px-6 py-4 flex items-center justify-between`}>
            <span className="text-xs font-bold text-white/70 tracking-wider">무료 체험 안내</span>
            <span className="text-xs font-mono text-white/80 bg-white/10 px-2 py-0.5 rounded">{current.badge}</span>
          </div>

          {/* 콘텐츠 */}
          <div className="px-8 py-6">
            {/* 이미지 영역 */}
            {current.image && (
              <div className="mb-5 rounded-xl overflow-hidden border border-slate-700 shadow-lg">
                <img src={current.image} alt={current.title} className="w-full h-auto max-h-72 object-contain" loading="eager" />
              </div>
            )}

            {/* 아이콘 (마지막 단계) */}
            {!current.image && (
              <div className="mb-5 flex justify-center">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-lg`}>
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                  </svg>
                </div>
              </div>
            )}

            {/* 하이라이트 태그 */}
            <div className="mb-3">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${colors.text} bg-slate-800 border ${colors.border}`}>
                {current.highlight}
              </span>
            </div>

            <h3 className="text-xl font-bold text-white mb-2">{current.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{current.description}</p>
          </div>

          {/* 하단 버튼 */}
          <div className="px-8 pb-6 flex items-center justify-between">
            {/* 스텝 인디케이터 */}
            <div className="flex gap-2">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === step ? `w-6 ${colors.glow}` : i < step ? 'w-3 bg-slate-500' : 'w-3 bg-slate-700'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-400 bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  이전
                </button>
              )}
              <button
                onClick={handleNext}
                className={`px-5 py-2 rounded-lg text-sm font-bold text-white ${colors.btn} transition-colors shadow-lg`}
              >
                {isLast ? '체험 시작!' : '다음'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// URL 의 ?code=CLS-... (기관 좌석 코드) 추출. 그 외/없으면 ''.
function urlSeatCode() {
  try {
    if (typeof window === 'undefined') return '';
    const c = (new URLSearchParams(window.location.search).get('code') || '').trim().toUpperCase();
    return c.startsWith('CLS-') ? c : '';
  } catch {
    return '';
  }
}

// 주소창에서 ?code= 를 지운다.
//
// 좌석 코드는 그 자리의 열쇠고, 주소창은 화면에 늘 떠 있다. 강의자 화면은 대개 빔에 물려
// 있어서, 입력칸과 헤더 배지를 가려도 여기서 그대로 샌다. 개인 경로는 이미 이렇게 하고
// 있었다(licenseService 의 consumeAdminURLPayload) — 기관 좌석 경로에만 그 줄이 없었다.
//
// ⚠️ **code 만 지우고 나머지 파라미터는 둔다.** ?preview·?k·?prototype·?legacy 를 App 이 읽고
//    ?join= 은 입장 흐름이 렌더마다 읽는다. 쿼리를 통째로 날리면 그쪽이 조용히 깨진다.
function stripSeatCodeFromUrl() {
  try {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('code')) return;
    url.searchParams.delete('code');
    const qs = url.searchParams.toString();
    window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : '') + (url.hash || ''));
  } catch { /* 주소창을 못 고쳐도 입장을 막지는 않는다 */ }
}

// URL 의 ?join=JOIN-... (반 입장 코드) 추출. QR 하나로 반 전체가 들어오는 경로다.
// 좌석 코드(CLS-)와 모양이 달라야 한다 — 닮으면 담당자가 좌석 코드를 QR 로 뿌리게 되고,
// 그건 한 자리를 반 전체에 뿌리는 사고다.
function urlJoinCode() {
  try {
    if (typeof window === 'undefined') return '';
    const c = (new URLSearchParams(window.location.search).get('join') || '').trim().toUpperCase();
    return c.startsWith('JOIN-') ? c : '';
  } catch {
    return '';
  }
}

// 입장 가능한 "현재 세션"이 URL 좌석 코드와 일치하는지.
// URL 에 다른 좌석 코드가 있으면, 잔존(공유 쿠키 복원 등) 라이선스로 입장하면 안 된다.
function sessionMatchesUrlSeat() {
  if (!isLicenseValid()) return false;
  const link = urlSeatCode();
  if (!link) return true; // URL 에 좌석 코드 없음 → 기존 세션 그대로
  const cur = getLicenseInfo();
  return (cur?.code || '').toUpperCase() === link;
}

/* ── 쇼케이스 카드(장식) ──
   LicenseGate 함수 **밖**에 둔다. 예전엔 안에서 정의돼, 코드칸에 한 글자 칠 때마다 새 컴포넌트로 취급돼
   통째로 다시 만들어졌다 — 움직이는 그림이 매번 처음부터 다시 돌았다.
   ready 전에는 같은 크기의 빈 틀만 둔다: 그림은 코드칸이 뜬 뒤 브라우저가 한가할 때 받는다
   (첫 화면 대역폭을 코드칸 JS 에 양보 · 틀이 먼저 있어 화면이 들썩이지 않는다). */
const SHOWCASE_RATIO = { '/showcase-left.gif': 272 / 500, '/showcase-right.gif': 373 / 423 };
const ShowcaseCard = ({ src, alt, angle, width, opacity = 1, zIndex = 0, offsetY = 0, ready = true }) => {
  const baseTransform = `rotateY(${angle}deg) translateY(${offsetY}px)`;
  const height = Math.round(width * (SHOWCASE_RATIO[src] || 0.6));
  return (
    <div
      className="hidden lg:flex items-center justify-center flex-shrink-0"
      style={{ perspective: '1000px', zIndex }}
    >
      <div
        className="rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10"
        style={{
          transform: baseTransform,
          transition: 'transform 0.5s cubic-bezier(.25,.46,.45,.94), opacity 0.4s ease',
          opacity,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = `rotateY(${angle * 0.3}deg) translateY(${offsetY}px) scale(1.04)`; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = baseTransform; }}
      >
        {/* 장식 이미지다. 코드 입력칸이 뜨는 것을 이게 막으면 안 된다.
            · gif 두 장이 3.0MB 였다 → animated webp 1.0MB → 2026-09-30 다시 구워 339KB
              (크로스페이드 9단계 → 3단계, 뺀 시간은 장면 정지 시간에 더해 총 재생 시간은 그대로).
              webp 를 모르는 브라우저만 원본 gif 로 간다.
            · lg 미만에서는 CSS 로 감추고 lazy — 감춰진 것은 받지 않는다.
            · 로딩 화면은 이 그림을 기다리지 않는다(public/index.html). */}
        {ready ? (
          <picture>
            <source srcSet={src.replace(/\.gif$/, '.webp')} type="image/webp" />
            <img
              src={src}
              alt={alt}
              width={width}
              height={height}
              style={{ width: `${width}px`, height: `${height}px` }}
              className="object-cover"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />
          </picture>
        ) : (
          <div style={{ width: `${width}px`, height: `${height}px`, background: 'rgba(148,163,184,.08)' }} aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

// 무료 면접 미션 판 — 미니팹 센터가 코드 없이 무료 미션만 연다(공정기술·장비기술 탭에 하나씩). 랜딩 job-board FREE_TRIAL_URL 과 같은 주소.
const FREE_MISSIONS_URL = 'https://krsimul.semifabai.com/?ui=process-center&demo&landing=career';

const LicenseGate = ({ children }) => {
  // URL 좌석 코드가 현재 세션과 다르면 입장하지 않는다(이전 학생 진도 노출 방지).
  const [licensed, setLicensed] = useState(() => isLicenseValid() && sessionMatchesUrlSeat());
  // URL 좌석 코드로 자동 전환 검증 중인지 — 그동안 입력 폼 깜빡임 방지.
  const [bootVerifying, setBootVerifying] = useState(() => Boolean(urlSeatCode()) && !sessionMatchesUrlSeat());
  // 링크로 들어온 좌석 코드는 **첫 렌더에 한 번만** 집는다.
  // 아래에서 주소창을 지우기 때문에, 이펙트가 URL 을 매번 다시 읽으면 재실행 때 '링크 없음'
  // 으로 보여 검증이 통째로 건너뛰어진다(개발 StrictMode 는 이펙트를 두 번 돌린다).
  // 판정에 쓰는 값과 화면에서 지우는 값을 갈라 둔다.
  const [linkSeat] = useState(() => urlSeatCode());
  const [linkSeatIsCurrentSession] = useState(() => sessionMatchesUrlSeat());
  const [code, setCode] = useState('');
  // 코드칸을 가려 둘 것인가 — 기본은 가림. 아래 입력칸 주석에 이유를 적었다.
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 다른 기기가 이 코드를 넣어 이 화면이 닫혔다 — 오류가 아니라 사실 안내다(빨강 아님, 규칙 5).
  const [takenNotice, setTakenNotice] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [showOnboarding] = useState(false);   // «Day 1 체험» 문을 내려 여는 곳이 없다(2026-11-02) — 온보딩 화면은 남겨 둔다
  // QR 입장 — ?join= 로 들어온 학생은 코드가 아니라 **이름**을 넣는다.
  // 반 입장 코드 — QR(?join=) 로 오거나, 코드칸에 JOIN- 을 직접 친다. 둘 다 같은 화면이다.
  const [typedJoin, setTypedJoin] = useState('');
  const setJoinPrompt = setTypedJoin;
  const urlJoin = urlJoinCode();
  const joinCode = typedJoin || urlJoin;
  const [joinName, setJoinName] = useState('');
  const [joining, setJoining] = useState(false);
  // 같은 이름의 자리가 이미 있을 때 되묻는 상태 — { seatCode, seq, message }
  const [joinDup, setJoinDup] = useState(null);
  const [copied, setCopied] = useState(false);

  // 장식 그림은 코드칸이 뜬 뒤 브라우저가 한가할 때 받는다(ShowcaseCard 주석)
  const [decoReady, setDecoReady] = useState(false);
  useEffect(() => {
    const go = () => setDecoReady(true);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(go, { timeout: 1500 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(go, 300);
    return () => clearTimeout(t);
  }, []);

  // 온보딩 이미지 미리 로드 (버튼 클릭 전에 캐시)
  useEffect(() => {
    const imgs = ['/onboarding-center.webp', '/onboarding-course.webp'];
    imgs.forEach(src => { const img = new Image(); img.src = src; });
  }, []);

  // 한 코드 = 한 기기(lib/deviceSession.js) — 다른 기기가 이 코드를 넣었으면 이 화면을 닫는다.
  // 코드칸은 **비워 둔다**: 밀려난 쪽이 실습실 공용 PC 일 수 있다(채워 두면 다음 사람이 한 번에 가져간다).
  const closeBecauseTaken = useCallback(() => {
    clearLicense();
    setCode('');
    setError('');
    setTakenNotice(true);
    setLicensed(false);
  }, []);

  // 기관 세션 즉시 회수 보장: 캐시 라이선스로 입장했어도, 로드 시 좌석을 Firestore 와
  // 재대조해 좌석이 삭제/회수/만료됐으면 즉시 내보낸다. (개인 경로엔 영향 없음)
  // 다른 기기가 주인이면(taken) 닫고 그 사실을 알린다 — 재대조는 확인만 하고 주인을 가져오지 않는다.
  useEffect(() => {
    if (!licensed) return;
    const info = getLicenseInfo();
    if (!info?.isInstitution || !info.code) return;
    let cancelled = false;
    revalidateInstitutionSession(info.code).then((status) => {
      if (cancelled) return;
      if (status === 'invalid') {
        clearLicense();
        setLicensed(false);
      } else if (status === 'taken') {
        closeBecauseTaken();
      }
    });
    return () => { cancelled = true; };
  }, [licensed, closeBecauseTaken]);

  // 입장해 있는 동안 — 기관 좌석은 주인 문서를 지켜보고(즉시), 개인 코드는 GAS 에 다시 묻는다
  // (GAS 는 밀어주지 못한다: 탭이 보일 때·탭으로 돌아올 때·PERSONAL_RECHECK_MS 마다).
  // 학생·강의자 좌석 모두 같다(담당자 2026-10-27). 무료 체험은 코드가 없어 대상이 아니다.
  useEffect(() => {
    if (!licensed) return;
    const info = getLicenseInfo();
    if (!info?.code || info.isFreeTrial || info.plan === 'free-trial') return;
    let done = false;
    const onTaken = () => {
      if (done) return;
      done = true;
      closeBecauseTaken();
    };
    if (info.isInstitution) {
      const stop = watchSeatHolder(info.code, onTaken);
      return () => { done = true; stop(); };
    }
    const check = () => {
      if (done || document.visibilityState === 'hidden') return;
      validatePersonalSession().then((v) => { if (v === 'taken') onTaken(); });
    };
    check();
    const timer = setInterval(check, PERSONAL_RECHECK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      done = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [licensed, closeBecauseTaken]);

  // URL 파라미터 ?trial=true 는 무시 — 항상 코드 입력 화면을 먼저 보여줌
  // (이전: 자동으로 체험 온보딩을 띄웠으나, 일반 코드 사용자가 체험으로 진입하는 버그 유발)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('trial') === 'true') {
      // URL에서 파라미터만 제거 (체험 자동 진입 안 함)
      window.history.replaceState({}, '', window.location.pathname);
    }
    // 기관 좌석 링크(?code=CLS-...) 처리.
    const linkCode = linkSeat;
    if (!linkCode) return;
    setCode(linkCode);

    // URL 좌석 코드가 현재 세션과 같으면 그대로 둔다.
    // 다만 링크는 **이미 쓴 것**이니 주소창에서는 지운다 — 북마크로 두고 매일 여는 경우가
    // 여기라, 안 지우면 노출이 수업 내내 이어진다.
    if (linkSeatIsCurrentSession) { stripSeatCodeFromUrl(); return; }

    // URL 코드가 현재(잔존) 세션과 다르면 → URL 코드가 권위를 가진다.
    // 이전 라이선스(공유 쿠키 복원 등)를 버리고 URL 코드를 서버 재검증해 전환한다.
    // 이게 없으면 잔존 세션이 새 코드 URL 을 가로채 이전 학생 진도가 그대로 보인다.
    let cancelled = false;
    clearLicense();
    setLicensed(false);
    verifyInstitutionCode(linkCode, { via: 'link' })
      .then(() => {
        // 입장했으면 링크는 다 쓴 것이다 — 코드는 이미 저장돼 있어 새로고침도 그대로 된다.
        // (cancelled 여부와 무관하게 지운다 — 주소창은 이 컴포넌트 것이 아니다.)
        stripSeatCodeFromUrl();
        if (!cancelled) setLicensed(true);
      })
      // ⚠️ 실패했을 때는 **안 지운다.** 네트워크 오류로 한 번 튀었는데 주소까지 지우면
      //    새로고침으로 다시 시도할 길이 없어진다. 들어가지도 못하고 링크도 잃는 것이 더 나쁘다.
      .catch((err) => { if (!cancelled) setError(err.message || '좌석 코드 확인에 실패했습니다.'); })
      .finally(() => { if (!cancelled) setBootVerifying(false); });
    return () => { cancelled = true; };
    // 둘 다 첫 렌더에 고정된 값이라 이 이펙트는 여전히 한 번만 돈다(lint 를 끄지 않아도 된다).
  }, [linkSeat, linkSeatIsCurrentSession]);

  // QR 입장 제출 — 서버가 빈 자리를 하나 배정한다.
  //
  // ⚠️ **자동으로 넘어가지 않는다.** QR 은 폰으로 찍는데 실습은 노트북·태블릿으로 한다.
  //    바로 앱으로 넘기면 학생은 자기 코드가 뭔지 볼 기회가 없고, 노트북에서 주소를 다시
  //    열어 **자리를 하나 더** 먹는다. QR 은 못 옮겨도 코드 한 줄은 옮길 수 있다.
  const doJoin = async (name, opts) => {
    setJoining(true);
    setError('');
    setJoinDup(null);
    try {
      const lic = await joinClassByCode(joinCode, name, opts);
      setSuccessInfo({ success: true, name, seatCode: lic.code, typeName: lic.typeName });
    } catch (err) {
      if (err.code === 'name_taken') {
        // 실패가 아니다 — 본인인지 동명이인인지는 사람만 안다(규칙 2: 되묻는다).
        setJoinDup({ seatCode: err.seatCode, seq: err.seq, message: err.message });
      } else {
        setError(err.message || '자리를 배정하지 못했어요.');
      }
    }
    setJoining(false);
  };

  const handleJoin = (e) => {
    e.preventDefault();
    const name = joinName.replace(/\s+/g, ' ').trim();
    if (!name) { setError('이름을 입력해 주세요.'); return; }
    doJoin(name);
  };

  // "그 자리로 이어서" — 새 자리를 만들지 않고 이미 있는 자리를 이 기기에 물린다.
  const handleTakeExisting = async () => {
    setJoining(true);
    setError('');
    try {
      const lic = await takeJoinedSeat(joinCode, joinDup.seatCode);
      setJoinDup(null);
      setSuccessInfo({ success: true, name: joinName.trim(), seatCode: lic.code, typeName: lic.typeName, resumed: true });
    } catch (err) {
      setError(err.message || '그 자리로 들어가지 못했어요.');
    }
    setJoining(false);
  };

  const enterHere = () => window.location.replace(window.location.origin + window.location.pathname);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('이용권 코드를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    setTakenNotice(false);
    setSuccessInfo(null);

    try {
      // 반 입장 코드(JOIN-)를 코드칸에 넣는 사람이 있다 — QR 아래에 적혀 있고,
      // 폰 카메라가 QR 을 못 읽으면 그걸 받아 적는 게 자연스럽다.
      // 여기서 안 받으면 GAS 로 넘어가 "잘못된 코드"가 뜬다. 맞는 화면으로 보낸다.
      if (trimmed.toUpperCase().startsWith('JOIN-')) {
        setLoading(false);
        setCode('');
        setJoinPrompt(trimmed.toUpperCase());
        return;
      }

      // 기관 좌석 코드(CLS-)는 Firestore 서버 검증, 그 외는 기존 GAS 검증.
      if (trimmed.toUpperCase().startsWith('CLS-')) {
        const lic = await verifyInstitutionCode(trimmed, { via: 'code' });
        setSuccessInfo({
          success: true,
          message: '인증되었습니다',
          typeName: lic.typeName,
          daysRemaining: lic.daysRemaining,
        });
      } else {
        // 사람이 코드칸에 넣은 입장 — 이 기기가 코드를 갖는다(이전 기기는 닫힌다).
        const data = await verifyLicense(trimmed, { claimSession: true });
        setSuccessInfo(data);
      }

      // 1.5초 후 입장 — reload 로 앱을 새로 마운트한다.
      // (setLicensed(true) 만 하면 이전 체험 세션에서 초기화된 App 의 mode 가 남아
      //  정식 코드 입장인데도 바로 가이드 학습으로 튀는 버그가 있었다. reload 하면
      //  유효 라이선스가 이미 저장돼 있으므로 코드 폼 깜빡임 없이 곧장 앱으로 들어간다.)
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 라이선스 유효 → 자식(앱 본체) 렌더링
  if (licensed) {
    return children;
  }

  // URL 좌석 코드로 자동 전환 검증 중 — 입력 폼 깜빡임 없이 로딩만 보여준다.
  // (검증 실패 시 error 가 세팅되며 아래 입력 화면으로 떨어진다.)
  if (bootVerifying && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center gap-4">
        <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-slate-400 text-sm">좌석 코드를 확인하는 중...</p>
      </div>
    );
  }

  // ── QR 입장 화면 ──────────────────────────────────────────────────
  // ?join= 으로 들어온 학생은 코드를 모른다. 이름만 넣으면 서버가 빈 자리를 준다.
  // 코드 입력 화면을 보여주면 "코드가 뭔데요?"가 되고, 그 질문이 전부 강의자에게 간다.
  if (joinCode && !licensed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-7">
            <h1 className="text-2xl font-bold text-white">수업에 입장</h1>
            <p className="text-sm text-slate-400 mt-2">이름을 입력하면 자리가 배정됩니다.</p>
            <p className="text-[11px] font-mono text-slate-500 mt-2">{joinCode}</p>
          </div>

          {/* 코드칸에 잘못 친 사람에게 돌아갈 길을 준다. QR 로 온 사람에게는 갈 데가 없다
              (주소가 그 화면이라) — 그때는 안 띄운다. */}
          {typedJoin && !successInfo && (
            <button
              type="button"
              onClick={() => { setTypedJoin(''); setJoinDup(null); setError(''); }}
              className="mb-4 text-xs text-slate-400 hover:text-white"
            >
              ← 이용권 코드로 들어가기
            </button>
          )}

          {successInfo ? (
            /* 자리를 받은 뒤 **멈춰 선다.** 여기서 코드를 안 보여주면 학생은 노트북에서
               주소를 다시 열고, 그러면 자리가 하나 더 나간다. */
            <div className="bg-emerald-950/40 border border-emerald-800/50 rounded-xl p-6">
              <div className="text-emerald-300 font-semibold text-center">
                {successInfo.name}님, {successInfo.resumed ? '원래 자리로 들어갑니다' : '자리가 배정되었습니다'}
              </div>

              <div className="mt-5 rounded-lg border border-emerald-800/60 bg-slate-950/60 px-4 py-4 text-center">
                <div className="text-[11px] text-slate-400 tracking-wide">내 코드</div>
                <div className="mt-1.5 font-mono text-xl sm:text-2xl font-bold text-emerald-200 tracking-wide break-all">
                  {successInfo.seatCode}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(successInfo.seatCode)
                      .then(() => setCopied(true))
                      .catch(() => window.prompt('코드 복사:', successInfo.seatCode));
                  }}
                  className="mt-3 px-3 py-1.5 rounded-md text-xs font-semibold text-slate-200 border border-slate-600 hover:border-slate-400"
                >
                  {copied ? '복사됨' : '코드 복사'}
                </button>
              </div>

              {/* QR 은 옮길 수 없지만 코드 한 줄은 옮길 수 있다. 이 안내가 핵심이다. */}
              <div className="mt-4 text-[12.5px] text-amber-200/90 leading-relaxed bg-amber-950/25 border border-amber-800/40 rounded-lg px-3.5 py-3">
                노트북·태블릿에서 이어서 하시려면 <b>이 코드를 적어두세요.</b><br />
                그 기기에서는 QR 대신 <b>코드를 입력</b>하면 같은 자리로 들어옵니다.
              </div>

              <button
                type="button"
                onClick={enterHere}
                className="mt-5 w-full py-3.5 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-500"
              >
                이 기기에서 계속하기
              </button>
            </div>
          ) : joinDup ? (
            /* 같은 이름의 자리가 이미 있다. **판정하지 않고 되묻는다** —
               본인이 기기를 옮긴 건지 동명이인인지는 사람만 안다. */
            <div className="bg-slate-900/70 border border-amber-800/50 rounded-xl p-6">
              <div className="text-amber-200 font-semibold">{joinDup.message}</div>
              <div className="mt-2 text-[12.5px] text-slate-400 leading-relaxed">
                폰으로 먼저 들어오셨다면 <b className="text-slate-200">그 자리로 이어서</b> 하세요.
                진도와 기록이 그대로 남아 있습니다.
              </div>
              <div className="mt-3 font-mono text-sm text-emerald-200">
                {joinDup.seatCode}{joinDup.seq ? <span className="text-slate-500"> · {joinDup.seq}번</span> : null}
              </div>
              <button
                type="button"
                disabled={joining}
                onClick={handleTakeExisting}
                className="mt-4 w-full py-3 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
              >
                그 자리로 이어서 하기
              </button>
              <button
                type="button"
                disabled={joining}
                onClick={() => doJoin(joinName.replace(/\s+/g, ' ').trim(), { forceNew: true })}
                className="mt-2 w-full py-3 rounded-lg text-sm font-semibold text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-50"
              >
                다른 사람이에요 — 새 자리 받기
              </button>
              {error && <div className="mt-3 text-sm text-red-400">{error}</div>}
            </div>
          ) : (
            <form onSubmit={handleJoin} className="bg-slate-900/70 border border-slate-800 rounded-xl p-6">
              <label className="block text-xs text-slate-400 mb-2">이름</label>
              <input
                autoFocus
                value={joinName}
                onChange={(e) => setJoinName(e.target.value)}
                placeholder="예: 김민준"
                maxLength={40}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-slate-100 text-lg"
              />
              {error && <div className="mt-3 text-sm text-red-400">{error}</div>}
              <button
                type="submit"
                disabled={joining}
                className="mt-5 w-full py-3.5 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
              >
                {joining ? '자리를 받는 중…' : '입장하기'}
              </button>
              <div className="mt-4 text-xs text-slate-500 text-center leading-relaxed">
                입장하면 <b className="text-slate-400">내 코드</b>를 알려드려요.<br />
                노트북·태블릿에서는 그 코드로 들어오시면 같은 자리입니다.
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const licenseInfo = getLicenseInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4 overflow-hidden">
      {/* 배경 장식 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 opacity-5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 opacity-5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-400 opacity-[0.03] rounded-full blur-3xl" />
      </div>

      {/* 5열 거울 레이아웃: 바깥L | 안쪽L | 가운데 카드 | 안쪽R | 바깥R */}
      <div className="relative flex items-center justify-center w-full" style={{ maxWidth: '1500px' }}>

        {/* ── 왼쪽 바깥 (작고 + 많이 틸트 + 교차: right gif) ── */}
        <div className="hidden xl:flex" style={{ marginRight: '-30px' }}>
          <ShowcaseCard
            ready={decoReady}
            src="/showcase-right.gif"
            alt="공정 시뮬레이션"
            angle={22}
            width={200}
            opacity={0.55}
            zIndex={1}
            offsetY={20}
          />
        </div>

        {/* ── 왼쪽 안쪽 (크고 + 살짝 틸트) ── */}
        <div className="hidden lg:flex" style={{ marginRight: '-16px', zIndex: 2 }}>
          <ShowcaseCard
            ready={decoReady}
            src="/showcase-left.gif"
            alt="시뮬레이터 미리보기"
            angle={10}
            width={320}
            opacity={0.9}
            zIndex={2}
          />
        </div>

        {/* ── 가운데 메인 카드 (거울) ── */}
        <div className="w-full max-w-md flex-shrink-0" style={{ zIndex: 10 }}>
          <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-8 text-center relative">
              <a
                href="https://semifabai.com"
                target="_blank"
                rel="noopener noreferrer"
                title="semifabai.com 홈으로"
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                  text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 transition-all active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
                <span>홈</span>
              </a>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur rounded-2xl mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">
                Semiconductor Process Simulator
              </h1>
              <p className="text-blue-200 text-sm">
                이용권 코드를 입력하여 서비스에 접속하세요
              </p>
            </div>

            {/* 입력 폼 */}
            <div className="px-8 py-8">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label
                    htmlFor="license-code"
                    className="block text-sm font-medium text-slate-300 mb-2"
                  >
                    이용권 코드
                  </label>
                  {/* 코드는 자물쇠다 — 화면에 펼쳐 두지 않는다.
                      강의자는 빔 프로젝터에 물린 화면에서 자기 코드를 친다. 평문으로 두면 그 반
                      전원이 강의자 코드를 받아 적고, 그 코드로 들어오면 담기 권한·게이트 우회·입장 QR
                      제어까지 따라간다. 학생 코드도 남이 보면 그 자리(진도)를 가져간다.

                      ⚠️ 여기서는 **누가 치는지 알 방법이 없다** — 좌석의 role 은 검증이 끝나야 온다.
                         코드 모양도 강의자·학생이 같다(CLS-<rand4>-<키번호2>-<rand2>). 그래서 역할로 가르지
                         않고 **기본을 가림**으로 두고 보기 단추를 준다.
                      ⚠️ `-webkit-text-security` 로 흉내내지 않는다. 안 먹는 브라우저에서 조용히 평문이
                         되고, 그건 보이지 않는 실패다(규칙 2). type=password 는 어디서나 먹는다. */}
                  <div className="relative">
                    <input
                      id="license-code"
                      type={showCode ? 'text' : 'password'}
                      value={code}
                      onChange={(e) => {
                        setCode(e.target.value.toUpperCase());
                        if (error) setError('');
                      }}
                      placeholder="예: A9K472M1"
                      disabled={loading || !!successInfo}
                      className={`w-full px-12 py-3 text-lg font-mono tracking-widest text-center border-2 rounded-xl
                        bg-slate-800 text-slate-100 transition-colors duration-200 placeholder:text-slate-500 placeholder:tracking-normal placeholder:font-sans placeholder:text-base
                        ${error
                          ? 'border-red-700 focus:border-red-500 focus:ring-red-900/50'
                          : 'border-slate-600 focus:border-blue-500 focus:ring-blue-900/50'
                        }
                        focus:outline-none focus:ring-4
                        disabled:bg-slate-800/50 disabled:text-slate-500`}
                      autoFocus
                      autoComplete="off"
                      maxLength={20}
                    />
                    {/* 가려만 두면 오타를 못 찾는다 — 잠그지 말고 사람이 열게 한다. */}
                    <button
                      type="button"
                      onClick={() => setShowCode((v) => !v)}
                      // 검증이 끝난 뒤엔 펼 이유가 없다 — 입장 직전에 불 켜줄 필요가 없다.
                      disabled={loading || !!successInfo}
                      aria-pressed={showCode}
                      aria-label={showCode ? '코드 가리기' : '코드 보기'}
                      title={showCode ? '코드 가리기' : '코드 보기 — 화면을 함께 보고 있다면 주의하세요'}
                      className="absolute inset-y-0 right-0 px-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors
                        disabled:text-slate-600 disabled:hover:text-slate-600"
                    >
                      {showCode ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* 다른 기기에서 입장해 닫혔다는 안내 — 오류가 아니다(빨강·경고 아이콘 없음) */}
                {takenNotice && !error && !successInfo && (
                  <div role="status" className="mb-4 p-3 bg-slate-800/60 border border-slate-600/60 rounded-lg animate-fade-in">
                    <span className="text-sm text-slate-300">{TAKEN_NOTICE}</span>
                  </div>
                )}

                {/* 에러 메시지 */}
                {error && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-700/50 rounded-lg flex items-start gap-2 animate-fade-in">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <span className="text-sm text-red-400">{error}</span>
                  </div>
                )}

                {/* 성공 메시지 */}
                {successInfo && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-700/50 rounded-lg flex items-start gap-2 animate-fade-in">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-green-400">
                      <p className="font-medium">{successInfo.message}</p>
                      {successInfo.daysRemaining && (
                        <p className="mt-1 text-green-300">
                          {successInfo.typeName} | 남은 기간: {successInfo.daysRemaining}일
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !!successInfo}
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-200
                    ${loading || successInfo
                      ? 'bg-slate-600 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98] shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      확인 중...
                    </span>
                  ) : successInfo ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      입장 중...
                    </span>
                  ) : (
                    '코드 확인'
                  )}
                </button>
              </form>

              {/* 무료 체험 구분선 + 버튼 */}
              <div className="mt-5 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-slate-900 px-3 text-slate-500">또는</span>
                </div>
              </div>

              {/* 무료 체험 — 미니팹 하나. «Day 1 체험» 문은 내렸다(담당자 2026-11-02 «1일차 체험하기는 없애자 — 들어가는 문을 없앤다»).
                  체험 온보딩(TrialOnboarding)·체험 라이선스 코드는 그대로 둔다 — 문만 없앴다. */}
              {/* 무료 체험 2종 — 한 줄에 두 칸(미니팹 · 무료 면접 미션). «Day 1 체험» 칸은 내리고(2026-11-02 «들어가는 문을 없앤다») 그 자리에
                  무료 면접 미션(담당자 «무료 2개를 접근하게 연결 · 2개가 나오는 화면을 보여줘야») — 미니팹 센터의 무료 미션 판(공정기술·장비기술 탭)으로 간다.
                  미션 한 장 착지(&mission=)가 아니다: 한 장만 보이면 다른 무료 미션이 있는 줄 모른다. 체험 온보딩·체험 라이선스 코드는 그대로 둔다. */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <a
                  href="https://krsimul.semifabai.com/?demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2.5 px-3 rounded-xl font-semibold text-purple-200 border-2 border-purple-700/50
                    bg-purple-900/20 hover:bg-purple-900/40 hover:border-purple-600/70
                    active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5 text-sm"
                >
                  <span>🏭</span>
                  <span>미니팹 체험</span>
                </a>
                <a
                  href={FREE_MISSIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="free-missions"
                  className="py-2.5 px-3 max-sm:px-2 rounded-xl font-semibold text-emerald-200 border-2 border-emerald-700/50
                    bg-emerald-900/20 hover:bg-emerald-900/40 hover:border-emerald-600/70
                    active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5 max-sm:gap-1 text-sm max-sm:text-[13px] whitespace-nowrap"
                >
                  <span>🎯</span>
                  <span>무료 면접 미션</span>
                </a>
              </div>
              <p className="mt-2 text-xs text-slate-500 text-center">
                코드 없이 바로 체험 — 미니팹 연속 공정 · 면접 미션 2개(공정·장비)
              </p>

              {/* 개인에게 파는 것은 면접 미션(9,900원)뿐이다 — 3·7일 개인 이용권은 내렸다(담당자 2026-10-18).
                  예전엔 쓰지 않는 kr. 주소의 #pricing(옛 개인 이용권 표)으로 갔다. */}
              <a
                href="https://semifabai.com/missions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full mt-3 py-2.5 px-4 rounded-xl font-semibold text-blue-300 border border-blue-700/50
                  bg-blue-900/20 hover:bg-blue-900/40 hover:border-blue-600/70
                  active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
                면접 미션 보기
              </a>
            </div>

            {/* 하단 안내 */}
            <div className="px-8 pb-6">
              <div className="border-t border-slate-700 pt-4">
                <p className="text-xs text-slate-500 text-center leading-relaxed">
                  이메일로 발급받은 이용권 코드를 입력해주세요.
                  <br />
                  코드 문의: 구매처에 문의하시기 바랍니다.
                </p>
              </div>
            </div>
          </div>

          {/* 만료된 라이선스 안내 */}
          {licenseInfo && !isLicenseValid() && (
            <div className="mt-4 p-4 bg-yellow-900/30 border border-yellow-700/50 rounded-xl text-center animate-fade-in">
              <p className="text-sm text-yellow-200">
                이전 이용권({licenseInfo.typeName})이 만료되었습니다.
              </p>
              <p className="text-xs text-yellow-300/70 mt-1">
                새로운 코드를 입력해주세요.
              </p>
            </div>
          )}
        </div>

        {/* ── 오른쪽 안쪽 (크고 + 살짝 틸트) ── */}
        <div className="hidden lg:flex" style={{ marginLeft: '-16px', zIndex: 2 }}>
          <ShowcaseCard
            ready={decoReady}
            src="/showcase-right.gif"
            alt="공정 시뮬레이션 미리보기"
            angle={-10}
            width={320}
            opacity={0.9}
            zIndex={2}
          />
        </div>

        {/* ── 오른쪽 바깥 (작고 + 많이 틸트 + 교차: left gif) ── */}
        <div className="hidden xl:flex" style={{ marginLeft: '-30px' }}>
          <ShowcaseCard
            ready={decoReady}
            src="/showcase-left.gif"
            alt="시뮬레이터"
            angle={-22}
            width={200}
            opacity={0.55}
            zIndex={1}
            offsetY={20}
          />
        </div>
      </div>

      {/* 온보딩 오버레이 */}
      {showOnboarding && (
        <OnboardingOverlay
          onComplete={() => {
            activateFreeTrial();
            // reload()로 새로고침 → 체험 데이터 유지 (navigate와 구분)
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

export default LicenseGate;
